import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const WaveformContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  backgroundColor: 'rgba(0, 0, 0, 0.03)',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));

const Canvas = styled('canvas')({
  width: '100%',
  height: '100%',
});

/**
 * Audio waveform visualization component
 * @param {Object} props Component props
 * @param {boolean} props.isActive Whether audio is currently playing
 * @param {string} props.color Color of the waveform
 * @returns {JSX.Element} AudioWaveform component
 */
const AudioWaveform = ({ isActive = false, color = '#3f51b5' }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const barsRef = useRef([]);

  // Initialize the waveform bars
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const barCount = 40; // Number of bars in the waveform
    
    // Set canvas dimensions
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    
    // Initialize bars with random heights
    barsRef.current = Array(barCount).fill().map(() => ({
      height: Math.random() * 0.5 + 0.1, // Random height between 0.1 and 0.6
      direction: Math.random() > 0.5 ? 1 : -1, // Random direction
      speed: Math.random() * 0.02 + 0.01, // Random speed
    }));

    // Initial render
    renderWaveform();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Update animation when isActive changes
  useEffect(() => {
    if (isActive) {
      animateWaveform();
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      // Render static waveform
      renderWaveform(0.2);
    }
  }, [isActive]);

  // Animate the waveform
  const animateWaveform = () => {
    // Update bar heights
    barsRef.current = barsRef.current.map(bar => {
      let newHeight = bar.height + (bar.direction * bar.speed);
      
      // Reverse direction if reaching limits
      if (newHeight > 0.8 || newHeight < 0.1) {
        return {
          ...bar,
          direction: -bar.direction,
          height: Math.max(0.1, Math.min(0.8, newHeight)),
        };
      }
      
      return {
        ...bar,
        height: newHeight,
      };
    });
    
    // Render the updated waveform
    renderWaveform();
    
    // Continue animation
    animationRef.current = requestAnimationFrame(animateWaveform);
  };

  // Render the waveform to the canvas
  const renderWaveform = (staticHeight) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const barWidth = width / barsRef.current.length / 2;
    const barMargin = barWidth * 0.4;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Set color with opacity based on active state
    ctx.fillStyle = isActive ? color : `${color}80`;
    
    // Draw bars
    barsRef.current.forEach((bar, index) => {
      const x = index * (barWidth + barMargin) * 2 + barWidth;
      const barHeight = staticHeight !== undefined 
        ? height * staticHeight 
        : height * bar.height;
      
      // Draw bar
      ctx.beginPath();
      ctx.roundRect(
        x, 
        (height - barHeight) / 2, 
        barWidth, 
        barHeight,
        barWidth / 2
      );
      ctx.fill();
    });
  };

  return (
    <WaveformContainer>
      <Canvas ref={canvasRef} />
    </WaveformContainer>
  );
};

export default AudioWaveform;
